import { useMutation } from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  DownloadInvoicePdfParams,
  DownloadInvoicePdfResult,
  UseDownloadInvoicePdfResult,
  UseDownloadInvoicePdfOptions,
} from "./useDownloadInvoicePdf.types"

/**
 * Organization-specific hook for downloading invoice PDFs
 *
 * Downloads an invoice PDF. If the PDF doesn't exist, it will queue
 * generation and send the PDF to the customer via email.
 *
 * Note: This hook does NOT open the PDF itself. It only fetches the
 * presigned URL (or processing status) and returns it to the caller,
 * since opening a popup window must happen synchronously within the
 * user's click handler to avoid browser popup blockers.
 *
 * @returns Mutation hook that resolves to either a redirect URL or a processing status
 */
export const useDownloadInvoicePdf = (
  options?: UseDownloadInvoicePdfOptions
): UseDownloadInvoicePdfResult => {
  guardAppUsage("mimber-organization", "useDownloadInvoicePdf")

  const { server } = useServerContext()

  return useMutation({
    ...options,
    mutationFn: async ({
      uuid,
    }: DownloadInvoicePdfParams): Promise<DownloadInvoicePdfResult> => {
      const response = await server.get(`invoices/${uuid}/pdf`, {
        maxRedirects: 0,
        validateStatus: (status) => status === 200 || status === 202,
      })

      // If 200, the PDF already exists and we got a presigned URL
      if (response.status === 200) {
        const presignedUrl: string | undefined = response.data?.data?.url
        if (!presignedUrl) {
          throw new Error(
            "Invoice PDF download response did not include a presigned URL"
          )
        }
        return { type: "redirect", url: presignedUrl }
      }

      // If 202, PDF generation was queued and will be emailed once ready
      const message: string =
        response.data?.data?.message ??
        "PDF is being generated and will be emailed to the customer shortly."
      return { type: "processing", message }
    },
  })
}
